// Clientes.js

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as React from 'react';
// @mui
import { Container, Typography, Stack, Button, TablePagination, Tooltip } from '@mui/material';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
// Tabela
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { UserListToolbar } from '../sections/@dashboard/user';
import Iconify from '../components/iconify';

// api
import { validateUser, getClientesPagina } from './api';
import config from '../config.json';
import ModalAddCliente from './ModalAddCliente';
import PopoverFiltroKit from './PopoverFiltroKit';
import { cores } from '../styles/styleUtils';

// ----------------------------

const emptyCliente = { nome: '', cpf: '', telefone: '', ativo: true };

// Função de mascaramento de CPF/CNPJ — LGPD
const maskCpfCnpj = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length <= 11) {
    // CPF: ***.456.789-**
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '***.***.$3-**');
  }
  // CNPJ: **.***.***/0001-**
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '**.***/0001-**');
};

// ----------------------------------------------------------------------

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [filtro, setFiltro] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalClientes, setTotalClientes] = useState(0);

  useEffect(() => {
    fetchClientes();
  }, [filtro, page, rowsPerPage]);

  const fetchClientes = () => {
    getClientesPagina({ ...filtro, page: page + 1, limit: rowsPerPage })
      .then((dados) => {
        setClientes(dados.clientes);
        setTotalClientes(dados.totalClientes);
      })
      .catch((error) => {
        window.alert('Erro ao consultar banco de dados');
        console.error('Erro ao buscar dados da API:', error);
      });
  };

  // Validação de usuário
  const navigate = useNavigate();
  useEffect(() => {
    validateuser();
  }, []);
  const validateuser = async () => {
    const teste = await validateUser();
    if (!teste.ok) {
      localStorage.removeItem('userData');
      localStorage.removeItem('userToken');
      config.user = {};
      navigate('/', { replace: true });
      window.alert('Sessão expirada, faça o login novamente');
      return;
    }
    localStorage.setItem('userData', JSON.stringify(teste.usuario));
    config.user = teste.usuario;
  };

  // Modal
  const [open, setOpen] = React.useState(false);
  const [produto, setProduto] = React.useState(emptyCliente);
  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setProduto({ ...emptyCliente });
  };

  const onConfirmHandler = async () => {
    handleClose();
    fetchClientes();
    setProduto(null);
  };

  const handleDoubleClickTable = (row, index) => {
    const usuario = JSON.parse(localStorage.getItem('userData'));

    // Supervisores têm acesso a todos
    if (!usuario.supervisor) {
      const userId = usuario._id;
      const cadastradoPor = row.cadastradoPor;

      // Verificar se o usuário cadastrou ou tem proposta com este cliente
      // O backend já filtra os clientes visíveis — aqui bloqueamos a edição
      // de clientes que aparecem apenas por busca de texto (se aplicável)
      if (cadastradoPor && cadastradoPor !== userId) {
        window.alert('Você não tem permissão para editar este cliente.');
        return;
      }
    }

    setProduto({ ...row, index });
    setOpen(true);
  };

  const [filterValue, setFilterValue] = useState('');
  const handleFilter = (event) => {
    setFilterValue(event.target.value);
    setFiltro({ ...filtro, nome: event.target.value });
    setPage(0);
  };

  const [openFiltro, setOpenFiltro] = useState(null);
  const onConfirmFiltro = (filtroPopover) => {
    setFiltro(filtroPopover);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Helmet>
        <title> Clientes </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography variant="h4" sx={{ mb: 5 }}>
            Clientes
          </Typography>

          <Stack direction={'row'} gap={2}>
            <Button
              onClick={(e) => setOpenFiltro(e.currentTarget)}
              variant="contained"
              startIcon={<Iconify icon="eva:search-fill" />}
              sx={{
                height: 50,
                minWidth: 25,
                backgroundColor: 'MediumSeaGreen',
                '&:hover': { backgroundColor: 'SeaGreen' },
              }}
            />
            <Button
              variant="contained"
              startIcon={<Iconify icon="zondicons:add-solid" />}
              onClick={handleOpen}
              sx={{ height: 50, minWidth: 100 }}
            >
              Adicionar
            </Button>
          </Stack>
        </Stack>

        <UserListToolbar filterName={filterValue} onFilterName={handleFilter} sx={{ width: '80vw' }} />

        {/* ------------ Tabela de Clientes ------------ */}
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">Nome</TableCell>
                <TableCell align="center">CPF/CNPJ</TableCell>
                <TableCell align="center">Telefone</TableCell>
                <TableCell align="center">
                  <Tooltip title="Conformidade LGPD">
                    <span>LGPD</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientes.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: 'Azure' } }}
                  onDoubleClick={() => handleDoubleClickTable(row, index)}
                >
                  <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                    {row.nome}
                  </TableCell>

                  {/* CPF mascarado — LGPD */}
                  <TableCell align="center" sx={{ fontFamily: 'monospace', color: '#555' }}>
                    {maskCpfCnpj(row.cpf)}
                  </TableCell>

                  <TableCell align="center">{row.telefone}</TableCell>

                  {/* Indicador visual de consentimento LGPD */}
                  <TableCell align="center">
                    {row.lgpd?.consentimento ? (
                      <Tooltip title={`Consentimento registrado em ${new Date(row.lgpd.dataConsentimento).toLocaleDateString('pt-BR')} por ${row.lgpd.declaradoPor}`}>
                        <GppGoodIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Consentimento LGPD não registrado">
                        <GppBadIcon sx={{ color: cores.laranja, fontSize: 20 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalClientes}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Clientes por página:"
          labelDisplayedRows={({ from, to, count }) => `Mostrando de ${from} a ${to} de ${count}`}
        />

        {/* Modal Add */}
        {open && <ModalAddCliente open={open} produto={produto} onClose={handleClose} onConfirm={onConfirmHandler} />}

        {openFiltro ? (
          <PopoverFiltroKit
            filtro={filtro}
            open={openFiltro}
            onClose={() => setOpenFiltro(null)}
            onConfirm={onConfirmFiltro}
          />
        ) : null}
      </Container>
    </>
  );
}